import React, { PureComponent } from 'react';
import Ellipsis from '../common/Ellipsis';
import { generateDataAttr } from '../utils/generateDataAttr';
import { classes, st } from './Button.st.css';
import { ButtonProps } from './Button.types';
import {
  WixStyleReactDefaultsOverride,
  WixStyleReactDefaultsOverrideContext,
} from '../WixStyleReactDefaultsOverrideProvider';
import deprecationLog from '../utils/deprecationLog';
import ButtonCore from './ButtonCore';

class Button extends PureComponent<ButtonProps> {
  static contextType = WixStyleReactDefaultsOverrideContext;

  static displayName = 'Button';
  constructor(props: ButtonProps) {
    super(props);

    if (props.skin === 'inverted') {
      deprecationLog(
        '<Button/> - skin="inverted" is deprecated and will be removed in next major version, please use skin="standard" and priority="secondary" instead',
      );
    }
  }

  button: React.RefObject<HTMLButtonElement> = React.createRef();

  /**
   * Sets focus on the element
   */
  focus = (options?: FocusOptions) => {
    this.button.current && this.button.current.focus(options);
  };

  /**
   * Returns the size and position of the element
   */
  getBoundingClientRect = (): DOMRect | undefined => {
    return this.button.current?.getBoundingClientRect();
  };

  render() {
    const { button: buttonPropsDefaults } = this
      .context as WixStyleReactDefaultsOverride;

    const {
      skin = 'standard',
      priority = 'primary',
      size = buttonPropsDefaults.size,
      className,
      fullWidth,
      children,
      dataHook,
      ellipsis,
      showTooltip,
      internalDataHook,
      tooltipProps = {},
      ...rest
    } = this.props;

    return (
      <Ellipsis
        ellipsis={ellipsis}
        showTooltip={showTooltip}
        {...tooltipProps}
        render={({ ref, ellipsisClasses }) => (
          <ButtonCore
            {...rest}
            {...generateDataAttr(
              {
                ...this.props,
                size,
              },
              ['skin', 'size', 'priority'],
            )}
            ref={this.button}
            className={st(
              classes.root,
              {
                fluid: !!fullWidth,
                skin,
                priority,
                size,
                ellipsis: !!ellipsis,
              },
              className,
            )}
            data-hook={dataHook}
            data-hook-internal={internalDataHook}
            contentClassName={ellipsisClasses()}
            contentRef={ref}
          >
            {children}
          </ButtonCore>
        )}
      />
    );
  }
}

export default Button;
